import { normalizeLeaveHoursResolution, type LeaveHoursResolution } from './leave-hours-resolution';
import { HOURS_PER_LEAVE_DAY } from './leave-hours-constants';

/**
 * Default ordinary hours per leave day = 38h week / 5. This is the fallback
 * when a worker has no effective pay-profile or explicit daily-hours override.
 */
export { HOURS_PER_LEAVE_DAY } from './leave-hours-constants';

export const CANONICAL_LEAVE_TYPES = [
  'ANNUAL',
  'PERSONAL',
  'CARERS_EXTRA_GENERAL_STAFF',
  // Family and domestic violence leave, NES ss106A-106E. Its own bucket, never
  // folded into PERSONAL: 10 paid days granted IN FULL each 12-month period
  // rather than accrued, and it does not carry over. Summing it with personal
  // leave would overstate both entitlements.
  'FAMILY_DOMESTIC_VIOLENCE',
  'LONG_SERVICE',
  'COMPASSIONATE',
  'UNPAID',
  'PUBLIC_HOLIDAY',
  'PARENTAL',
  'COMMUNITY_SERVICE',
  // Accrued-balance types held in the leave registry (1.0.0) until the exact
  // accrual source is confirmed. Present here so a TOIL/ADO Xero balance row
  // normalises instead of throwing the moment the SQL summary emits one; the
  // SQL side (safe_canonical_worker_leave_request_type) only emits them once
  // 20271036000054 is applied, so nothing renders before switch-on.
  'TOIL',
  'ADO',
] as const;

export type CanonicalLeaveType = (typeof CANONICAL_LEAVE_TYPES)[number];
export type WorkerLeaveBalanceSource = 'xero' | 'accrual';
export type LeaveUnitType = 'Hours' | 'Days';
// 20271036000060: the windowed resolver adds 'roster' and 'roster_history'.
// The 1-arg summary still emits only the legacy three; the wider union keeps
// the two payloads assignable to one type.
export type HoursPerLeaveDaySource =
  | 'pay_profile'
  | 'custom_fields'
  | 'default'
  | 'roster'
  | 'roster_history';

const CANONICAL_LEAVE_TYPE_SET: ReadonlySet<string> = new Set(CANONICAL_LEAVE_TYPES);

const LEAVE_TYPE_ALIASES: Record<string, CanonicalLeaveType> = {
  ANNUAL: 'ANNUAL',
  ANNUAL_LEAVE: 'ANNUAL',
  PERSONAL: 'PERSONAL',
  PERSONAL_CARERS: 'PERSONAL',
  PERSONAL_CARER: 'PERSONAL',
  PERSONAL_CARERS_LEAVE: 'PERSONAL',
  PERSONAL_CARER_LEAVE: 'PERSONAL',
  PERSONAL_SICK: 'PERSONAL',
  PERSONAL_SICK_LEAVE: 'PERSONAL',
  SICK: 'PERSONAL',
  SICK_LEAVE: 'PERSONAL',
  CARERS_EXTRA_GENERAL_STAFF: 'CARERS_EXTRA_GENERAL_STAFF',
  // Must mirror the SQL taxonomy exactly. A spelling accepted by
  // canonical_worker_leave_type but missing here (or vice versa) is how a
  // balance row gets silently dropped from the summary.
  FAMILY_DOMESTIC_VIOLENCE: 'FAMILY_DOMESTIC_VIOLENCE',
  FAMILY_DOMESTIC_VIOLENCE_LEAVE: 'FAMILY_DOMESTIC_VIOLENCE',
  FAMILY_AND_DOMESTIC_VIOLENCE: 'FAMILY_DOMESTIC_VIOLENCE',
  FAMILY_AND_DOMESTIC_VIOLENCE_LEAVE: 'FAMILY_DOMESTIC_VIOLENCE',
  PAID_FAMILY_AND_DOMESTIC_VIOLENCE_LEAVE: 'FAMILY_DOMESTIC_VIOLENCE',
  PAID_FAMILY_DOMESTIC_VIOLENCE_LEAVE: 'FAMILY_DOMESTIC_VIOLENCE',
  DOMESTIC_VIOLENCE: 'FAMILY_DOMESTIC_VIOLENCE',
  DOMESTIC_VIOLENCE_LEAVE: 'FAMILY_DOMESTIC_VIOLENCE',
  FDV: 'FAMILY_DOMESTIC_VIOLENCE',
  FDV_LEAVE: 'FAMILY_DOMESTIC_VIOLENCE',
  EXTRA_CARERS_GENERAL_STAFF: 'CARERS_EXTRA_GENERAL_STAFF',
  GENERAL_STAFF_EXTRA_CARERS: 'CARERS_EXTRA_GENERAL_STAFF',
  CARERS_LEAVE: 'PERSONAL',
  CARER_LEAVE: 'PERSONAL',
  CARER_S_LEAVE: 'PERSONAL',
  LONG_SERVICE: 'LONG_SERVICE',
  LONG_SERVICE_LEAVE: 'LONG_SERVICE',
  COMPASSIONATE: 'COMPASSIONATE',
  COMPASSIONATE_LEAVE: 'COMPASSIONATE',
  BEREAVEMENT: 'COMPASSIONATE',
  BEREAVEMENT_LEAVE: 'COMPASSIONATE',
  UNPAID: 'UNPAID',
  UNPAID_LEAVE: 'UNPAID',
  PUBLIC_HOLIDAY: 'PUBLIC_HOLIDAY',
  PUBLIC_HOLIDAY_LEAVE: 'PUBLIC_HOLIDAY',
  PH_LEAVE: 'PUBLIC_HOLIDAY',
  PARENTAL: 'PARENTAL',
  PARENTAL_LEAVE: 'PARENTAL',
  COMMUNITY_SERVICE: 'COMMUNITY_SERVICE',
  COMMUNITY_SERVICE_LEAVE: 'COMMUNITY_SERVICE',
  // Registry keys only. The legacy request token TIME_IN_LIEU_TAKEN is
  // deliberately NOT aliased here: the request-side classifier
  // (safe_canonical_worker_leave_request_type) still buckets it as UNPAID
  // until 20271036000054, and aliasing it early would relabel existing rows.
  TOIL: 'TOIL',
  TIME_OFF_IN_LIEU: 'TOIL',
  ADO: 'ADO',
  ACCRUED_DAY_OFF: 'ADO',
};

function dateOnlyUtcMs(value: string): number {
  const [year, month, day] = value.split('-').map((part) => Number(part));
  if (!year || !month || !day) return NaN;
  return Date.UTC(year, month - 1, day);
}

export function countLeaveBusinessDays(startDate: string, endDate: string): number {
  const startMs = dateOnlyUtcMs(startDate);
  const endMs = dateOnlyUtcMs(endDate);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return NaN;
  if (endMs < startMs) return 0;

  let count = 0;
  for (let current = startMs; current <= endMs; current += 24 * 60 * 60 * 1000) {
    const day = new Date(current).getUTCDay();
    if (day !== 0 && day !== 6) count += 1;
  }
  return count;
}

export function calculateLeaveRequestTotalHours(
  startDate: string,
  endDate: string,
  hoursPerLeaveDay = HOURS_PER_LEAVE_DAY,
): number {
  return countLeaveBusinessDays(startDate, endDate) * hoursPerLeaveDay;
}

export interface ResolveHoursPerLeaveDayInput {
  contractedHoursPerWeek?: number | string | null;
  ordinaryDaysOfWeek?: readonly unknown[] | null;
  ordinaryHoursCycleDays?: number | string | null;
  ordinaryHoursCycleHours?: number | string | null;
  maximumDailyOrdinaryHours?: number | string | null;
  customFullTimeDailyOrdinaryHours?: number | string | null;
}

export interface ResolvedHoursPerLeaveDay {
  hoursPerLeaveDay: number;
  source: HoursPerLeaveDaySource;
  ordinaryDaysPerWeek: number | null;
}

export function resolveHoursPerLeaveDay(
  input: ResolveHoursPerLeaveDayInput | null | undefined,
): ResolvedHoursPerLeaveDay {
  const contractedHoursPerWeek = numberOrNull(input?.contractedHoursPerWeek);
  const ordinaryDaysPerWeek = resolveOrdinaryDaysPerWeek(input);
  if (
    contractedHoursPerWeek !== null &&
    contractedHoursPerWeek > 0 &&
    ordinaryDaysPerWeek !== null &&
    ordinaryDaysPerWeek > 0
  ) {
    const maximumDailyOrdinaryHours = numberOrNull(input?.maximumDailyOrdinaryHours);
    const uncappedHoursPerDay = contractedHoursPerWeek / ordinaryDaysPerWeek;
    const cappedHoursPerDay =
      maximumDailyOrdinaryHours !== null && maximumDailyOrdinaryHours > 0
        ? Math.min(uncappedHoursPerDay, maximumDailyOrdinaryHours)
        : uncappedHoursPerDay;
    if (Number.isFinite(cappedHoursPerDay) && cappedHoursPerDay > 0) {
      return {
        hoursPerLeaveDay: roundLeaveHours(cappedHoursPerDay),
        source: 'pay_profile',
        ordinaryDaysPerWeek: roundLeaveHours(ordinaryDaysPerWeek),
      };
    }
  }

  const customHours = numberOrNull(input?.customFullTimeDailyOrdinaryHours);
  if (customHours === 8 || customHours === 10) {
    return {
      hoursPerLeaveDay: customHours,
      source: 'custom_fields',
      ordinaryDaysPerWeek: null,
    };
  }

  return {
    hoursPerLeaveDay: HOURS_PER_LEAVE_DAY,
    source: 'default',
    ordinaryDaysPerWeek: null,
  };
}

function resolveOrdinaryDaysPerWeek(
  input: ResolveHoursPerLeaveDayInput | null | undefined,
): number | null {
  const ordinaryDaysOfWeek = input?.ordinaryDaysOfWeek;
  const explicitDays = Array.isArray(ordinaryDaysOfWeek) ? ordinaryDaysOfWeek.length : null;
  if (explicitDays !== null && explicitDays > 0) return explicitDays;

  return null;
}

function roundLeaveHours(value: number): number {
  return Math.round(value * 10_000) / 10_000;
}

export const LEAVE_TYPE_DISPLAY_LABELS: Record<CanonicalLeaveType, string> = {
  ANNUAL: 'Annual leave',
  PERSONAL: "Personal / carer's leave",
  CARERS_EXTRA_GENERAL_STAFF: "Extra carer's leave (General Staff)",
  FAMILY_DOMESTIC_VIOLENCE: 'Family and domestic violence leave',
  LONG_SERVICE: 'Long service leave',
  COMPASSIONATE: 'Compassionate leave',
  UNPAID: 'Unpaid leave',
  PUBLIC_HOLIDAY: 'Public holiday leave',
  PARENTAL: 'Parental leave',
  COMMUNITY_SERVICE: 'Community service leave',
  TOIL: 'Time off in lieu',
  ADO: 'Accrued day off',
};

/** Display guidance only; does not alter the balance or entitlement calculation. */
export const LONG_SERVICE_LEAVE_INDICATIVE_NOTE =
  'Indicative only. Long service leave is calculated under the Long Service Leave Act when it is taken or paid out.';

export interface WorkerLeaveBalanceRow {
  /** Derived from the canonical leave type, never from the payroll payload. */
  isIndicative: boolean;
  statura_leave_type: CanonicalLeaveType;
  canonical_leave_type: CanonicalLeaveType;
  raw_leave_type: string | null;
  display_name: string;
  source: WorkerLeaveBalanceSource;
  type_of_units: LeaveUnitType;
  xero_units: number | null;
  in_flight_units: number;
  effective_units: number | null;
  fetched_at: string | null;
  /** Mirror provenance when source is xero — e.g. XERO_ABSENT after refresh provisioning. */
  provenance: string | null;
  stale: boolean;
  degraded: boolean;
  needs_sync: boolean;
  accrued: number | null;
  taken: number | null;
  balance: number | null;
  projected_balance: number | null;
  projected_units: number | null;
  as_at_date: string | null;
  /** Present when leave was settled via payslip adoption (PAID_EXTERNALLY). */
  settlement_note?: string | null;
}

export interface WorkerLeaveBalanceSummary {
  staff_id: string | null;
  tenant_id: string | null;
  source: WorkerLeaveBalanceSource;
  xero_connected: boolean;
  degraded: boolean;
  needs_sync: boolean;
  unmapped_types_present: boolean;
  generated_at: string | null;
  hours_per_leave_day: number;
  hours_per_leave_day_source: HoursPerLeaveDaySource;
  /**
   * Present only on the windowed overload
   * `get_worker_leave_balance_summary_controlled(staff, start, end)`: the
   * roster-aware resolution for that window. Null on the 1-arg summary and
   * when the payload predates 20271036000060 (deploy-before-apply).
   */
  leave_hours_resolution: LeaveHoursResolution | null;
  balances: WorkerLeaveBalanceRow[];
}

function canonicalizeToken(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

export function canonicalLeaveType(value: string | null | undefined): CanonicalLeaveType {
  const token = canonicalizeToken(String(value ?? ''));
  const mapped = LEAVE_TYPE_ALIASES[token];
  if (mapped) return mapped;
  if (CANONICAL_LEAVE_TYPE_SET.has(token)) return token as CanonicalLeaveType;
  throw new Error(`Unknown leave type: ${String(value ?? '')}`);
}

export function isCanonicalLeaveType(
  value: string | null | undefined,
): value is CanonicalLeaveType {
  return typeof value === 'string' && CANONICAL_LEAVE_TYPE_SET.has(value);
}

export function leaveTypeDisplayName(value: string | null | undefined): string {
  return LEAVE_TYPE_DISPLAY_LABELS[canonicalLeaveType(value)];
}

export function normalizeLeaveUnitType(value: string | null | undefined): LeaveUnitType {
  const token = String(value ?? 'Hours')
    .trim()
    .toLowerCase();
  if (token === '' || token === 'hour' || token === 'hours' || token === 'h') return 'Hours';
  if (token === 'day' || token === 'days' || token === 'd') return 'Days';
  throw new Error(`Unknown leave unit type: ${String(value ?? '')}`);
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function booleanValue(value: unknown): boolean {
  return value === true;
}

function sourceValue(value: unknown): WorkerLeaveBalanceSource {
  return String(value ?? '').toLowerCase() === 'xero' ? 'xero' : 'accrual';
}

function hoursPerLeaveDaySourceValue(value: unknown): HoursPerLeaveDaySource {
  const normalized = String(value ?? '').toLowerCase();
  if (
    normalized === 'pay_profile' ||
    normalized === 'custom_fields' ||
    normalized === 'roster' ||
    normalized === 'roster_history'
  ) {
    return normalized;
  }
  return 'default';
}

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function numberOrZero(value: unknown): number {
  return numberOrNull(value) ?? 0;
}

export function normalizeWorkerLeaveBalanceSummary(
  input: unknown,
  expectedWindow?: { startDate: string; endDate: string },
): WorkerLeaveBalanceSummary {
  const summary = asRecord(input);
  const source = sourceValue(summary.source);
  const needsSync = booleanValue(summary.needs_sync ?? summary.needsSync);
  const unmappedTypesPresent = booleanValue(
    summary.unmapped_types_present ?? summary.unmappedTypesPresent,
  );
  const degraded = booleanValue(summary.degraded) || unmappedTypesPresent;
  const rows = Array.isArray(summary.balances) ? summary.balances : [];
  const hoursPerLeaveDay =
    numberOrNull(summary.hours_per_leave_day ?? summary.hoursPerLeaveDay) ?? HOURS_PER_LEAVE_DAY;
  const hoursPerLeaveDaySource = hoursPerLeaveDaySourceValue(
    summary.hours_per_leave_day_source ?? summary.hoursPerLeaveDaySource,
  );

  return {
    staff_id: stringOrNull(summary.staff_id ?? summary.staffId),
    tenant_id: stringOrNull(summary.tenant_id ?? summary.tenantId),
    source,
    xero_connected: booleanValue(summary.xero_connected ?? summary.xeroConnected),
    degraded,
    needs_sync: needsSync,
    unmapped_types_present: unmappedTypesPresent,
    generated_at: stringOrNull(summary.generated_at ?? summary.generatedAt),
    hours_per_leave_day: hoursPerLeaveDay,
    hours_per_leave_day_source: hoursPerLeaveDaySource,
    leave_hours_resolution: normalizeLeaveHoursResolution(
      summary.leave_hours_resolution ?? summary.leaveHoursResolution,
      expectedWindow,
    ),
    balances: rows.map((rowInput) => {
      const row = asRecord(rowInput);
      const canonical = canonicalLeaveType(
        (row.canonical_leave_type ??
          row.canonicalLeaveType ??
          row.statura_leave_type ??
          row.staturaLeaveType ??
          row.leave_type ??
          row.leaveType) as string | null | undefined,
      );
      const rowSource = sourceValue(row.source ?? source);
      const rowDegraded = booleanValue(row.degraded) || degraded;
      const rowNeedsSync = booleanValue(row.needs_sync ?? row.needsSync) || needsSync;
      const effectiveUnits = numberOrNull(row.effective_units ?? row.effectiveUnits ?? row.balance);
      const projectedBalance = numberOrNull(row.projected_balance ?? row.projectedBalance);
      const typeOfUnits = normalizeLeaveUnitType(
        stringOrNull(row.type_of_units ?? row.typeOfUnits) ?? undefined,
      );

      return {
        statura_leave_type: canonical,
        canonical_leave_type: canonical,
        raw_leave_type: stringOrNull(
          row.raw_leave_type ??
            row.rawLeaveType ??
            row.raw_statura_leave_type ??
            row.rawStaturaLeaveType,
        ),
        display_name: leaveTypeDisplayName(canonical),
        isIndicative: canonical === 'LONG_SERVICE',
        source: rowSource,
        type_of_units: typeOfUnits,
        xero_units: numberOrNull(row.xero_units ?? row.xeroUnits),
        in_flight_units: numberOrZero(row.in_flight_units ?? row.inFlightUnits),
        effective_units: effectiveUnits,
        fetched_at: stringOrNull(row.fetched_at ?? row.fetchedAt),
        provenance: stringOrNull(row.provenance),
        stale: booleanValue(row.stale),
        degraded: rowDegraded,
        needs_sync: rowNeedsSync,
        accrued: numberOrNull(row.accrued),
        taken: numberOrNull(row.taken),
        balance: numberOrNull(row.balance ?? row.effective_units ?? row.effectiveUnits),
        projected_balance: projectedBalance,
        projected_units:
          numberOrNull(row.projected_units ?? row.projectedUnits) ?? projectedBalance,
        as_at_date: stringOrNull(row.as_at_date ?? row.asAtDate),
        settlement_note: stringOrNull(row.settlement_note ?? row.settlementNote),
      };
    }),
  };
}
